import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import ConfirmButton from './ConfirmButton';
import {
    extraUploadConfirmationNotNeeded,
    uploadFile,
} from '../../actions/fileUploads';
import useAuthentication from '../../hooks/useAuthentication';
import {
    getFileUploads,
    isExtraUploadConfirmationNeeded,
} from '../../reducers';
import { FileUploadState } from '../../reducers/fileUploads';
import { fileUploadVariablesType } from '../../types';
import ConfirmPopup from '../popups/ConfirmPopup';

UploadFilesButton.propTypes = {
    fileDescriptors: PropTypes.arrayOf(PropTypes.shape({
        file: PropTypes.instanceOf(File),
        isTooLarge: PropTypes.bool,
    })).isRequired,
    uploadPath: PropTypes.string.isRequired,
    disabled: PropTypes.bool.isRequired,
    variables: fileUploadVariablesType,
    onUpload: PropTypes.func,
    extraConfirmationMessage: PropTypes.string.isRequired,
    onCancelTokenSourceChanged: PropTypes.func.isRequired,
};

function UploadFilesButton(props) {
    const {
        fileDescriptors,
        uploadPath,
        disabled,
        variables = [],
        onUpload = null,
        extraConfirmationMessage,
        onCancelTokenSourceChanged,
    } = props;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { accessToken } = useAuthentication();
    const fileUploads = useSelector(getFileUploads);
    const isExtraConfirmationNeeded = useSelector(isExtraUploadConfirmationNeeded);
    const isDisabled = disabled || fileUploads.some((fileUpload) => fileUpload.state !== FileUploadState.INIT);
    const upload = () => {
        fileDescriptors.forEach(
            (fileDescriptor) => dispatch(
                uploadFile(fileDescriptor.file, fileDescriptor.cancelTokenSource, uploadPath, accessToken, variables, onUpload),
            ),
        );
    };
    const uploadWithExtraConfirmation = () => {
        dispatch(extraUploadConfirmationNotNeeded());
        const vars = [...variables, { name: 'extraConfirmation', value: true }];
        fileDescriptors.forEach((fileDescriptor) => {
            const newCancelTokenSource = axios.CancelToken.source();
            onCancelTokenSourceChanged(fileDescriptor.file)(newCancelTokenSource);
            dispatch(uploadFile(fileDescriptor.file, newCancelTokenSource, uploadPath, accessToken, vars, onUpload));
        });
    };
    const onCancelExtraConfirmation = () => dispatch(extraUploadConfirmationNotNeeded());
    return (
        <>
            <ConfirmButton
                className="upload-files-button button button-bordered"
                disabled={isDisabled}
                message={t('file-upload.confirm-file-upload', { count: fileDescriptors.length })}
                onConfirm={upload}
            >
                <FontAwesomeIcon icon="upload" />&nbsp;{t('button.upload')}
            </ConfirmButton>
            {isExtraConfirmationNeeded && (
                <ConfirmPopup
                    onConfirm={uploadWithExtraConfirmation}
                    onCancel={onCancelExtraConfirmation}
                    message={extraConfirmationMessage}
                />
            )}
        </>
    );
}

export default UploadFilesButton;
